import React from 'react';
import { Box, List, ListItem, ListItemButton, ListItemText, Typography } from '@mui/material';

export interface ChatConversationSummary {
  id: string;
  /** Participant display label (one conversation per participant). */
  title: string;
  /** Last-activity line shown in the conversation list. */
  preview: string;
  /** The conversation has messages the researcher hasn't read yet. */
  unread?: boolean;
  /** Participant's app predates chat; the preview shows the update-required copy. */
  chatDisabled?: boolean;
}

export interface ConversationListProps {
  conversations: ChatConversationSummary[];
  activeId: string | null;
  onSelect: (conversationId: string) => void;
}

/**
 * The chat sidebar: one row per participant conversation. Unread conversations
 * get a dot badge and a bold title. A conversation whose participant runs a
 * pre-chat app version stays selectable (researcher-side history still
 * decrypts) but shows the update-required copy as its italic preview.
 */
export function ConversationList({ conversations, activeId, onSelect }: ConversationListProps) {
  return (
    <List
      aria-label="Conversations"
      disablePadding
      sx={{ width: 280, flexShrink: 0, borderRight: '1px solid', borderColor: 'divider', overflowY: 'auto' }}
    >
      {conversations.length === 0 && (
        <ListItem>
          <ListItemText
            secondary="No conversations yet."
            secondaryTypographyProps={{ sx: { fontSize: 12.5 } }}
          />
        </ListItem>
      )}
      {conversations.map((conversation) => (
        <ListItem key={conversation.id} disablePadding>
          <ListItemButton
            selected={conversation.id === activeId}
            onClick={() => onSelect(conversation.id)}
            sx={{
              alignItems: 'flex-start',
              '&.Mui-selected': {
                borderLeft: '3px solid',
                borderLeftColor: 'primary.main',
                bgcolor: 'action.selected',
              },
              // Keep the update-required copy readable inside the muted row.
              ...(conversation.chatDisabled ? { opacity: 0.65 } : {}),
            }}
          >
            <ListItemText
              primary={
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                  <Typography component="span" sx={{ fontWeight: conversation.unread ? 700 : 500, fontSize: 14 }}>
                    {conversation.title}
                  </Typography>
                  {conversation.unread ? (
                    <Box
                      component="span"
                      aria-label="Unread messages"
                      sx={{
                        bgcolor: 'primary.main',
                        borderRadius: '50%',
                        width: 10,
                        height: 10,
                        flexShrink: 0,
                      }}
                    />
                  ) : null}
                </Box>
              }
              secondary={conversation.preview}
              secondaryTypographyProps={{
                sx: {
                  fontSize: 12.5,
                  ...(conversation.chatDisabled
                    ? { fontStyle: 'italic' }
                    : { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }),
                },
              }}
            />
          </ListItemButton>
        </ListItem>
      ))}
    </List>
  );
}
